import { IExclusiveNewsletterConfig } from '../../../../domain/conf/identity'
import { logger, loggerEnabled } from '../../../../utils/common/logger'
import { DOMEvents } from '../../../../utils/dom'
import { DOMUtils } from '../../../../utils/dom'
import { Store } from '../../../../utils/storage/keys'
import { LocalStore } from '../../../../utils/storage/local'
import { AdEvents } from '../../../ads/ad/events'
import { AnalyticsService } from '../../../analytics'
import { IProfile } from '../../../common/profile'
import { IIdentityLogoConfig, ISubscription, SSOScreen } from '../adapter'
import { LoginRadiusAdapter } from '../loginradius'
import { LoginService } from '../loginradius/services/login-service'
import SailthruAdapter from '../sailthru'
import { AbTestAssignedGroup, WallType } from '../sophi'

export interface IPianoApiResponse {
    code: number
    count?: number
    data?: object[]
    limit: number
    offset: number
    total: number
    conversions?: IPianoConversion[]
}

export interface IPianoApiAccessItem {
    // `expire_date` is not always available in the `/access/list` response.
    // It's only available in `conversion/list` response
    // All already expired supscription terms are excluded from `/access/list` response
    expire_date?: number
    resource: {
        aid: string
        rid: string
    },
    term: IPianoTerm
    start_date: number
}

export interface IPianoResource {
    rid: string
    aid: string
    name: string
    description: string
    image_url?: string
}

export interface IPianoTerm {
    aid: string
    term_id: string
    resource?: IPianoResource
    name?: string
    description?: string
    type?: string
}

export interface IPianoUserAccess {
    granted: boolean
    start_date: number
    expire_date?: number
}

export interface IPianoConversion {
    aid: string
    term?: IPianoTerm
    term_conversion_id?: string
    type?: string
    user_access: IPianoUserAccess
}

export interface IWindow extends Window {
    sophi: any
    demeter: any,
    tp: any
}

declare let window: IWindow

enum PianoCheckoutState {
    ALREADY_HAS_ACCESS = 'alreadyHasAccess',
    CHECKOUT_COMPLETED = 'checkoutCompleted',
    VOUCHER_REDEMPTION_COMPLETED = 'voucherRedemptionCompleted',
    CLOSE = 'close'

}

enum PianoModuleName {
    PREMIUM_SHOW_OFFER = 'Premium Show Offer',
    REGISTRATION_ONLY_USER_SEGMENT = 'Registration Only User segment'
}

enum FocusableElements {
    MAINSITE = 'a, main, input, select, textarea, details, audio, video, [tabindex="0"], button:not(.tp-close)',
    MODAL = '.tp-iframe-wrapper.tp-active',
    MAIN = 'main'
}

export abstract class BasePianoSubscriberService {

    public static DOM = {
        class: {
            pageExclusiveNewsletter: 'page-exclusive-newsletter',
            pageOnboardExclusiveNewsletter: 'page-onboard-exclusive-newsletter',
            pageRegistrationSuccess: 'page-registration-success'
        }
    }
    protected openCheckoutAfterLogin = false
    protected pianoEnabled = true
    protected pianoPreconfigured: boolean
    protected subscribeButton: HTMLButtonElement
    protected uid: string

    protected pianoEndpoints = {
        access: '/access/list',
        conversions: '/conversion/list',
        externalCreate:'/conversion/external/create',
        register: '/conversion/registration/create'
    }
    protected pianoExpUrl = '//experience.tinypass.com/xbuilder/experience/load?aid='
    protected pianoSandboxUrl = '//sandbox.tinypass.com/xbuilder/experience/load?aid='
    protected pianoReady: Promise<boolean>
    protected registrantTermId: string
    protected userToken: string
    protected pianoUser: {
        email?: string
    }
    protected meterData: {
        expired?: boolean,
        maxViews?: number,
        views?: number
    }

    public abstract getAccess(): Promise<ISubscription[]>

    public abstract getConversions(): any

    /**
     * Close Piano modal
     */
    public closeCheckoutModal() {
        window.tp.offer.close()
        if(window.location.pathname === '/my-offers') {
            this.setFocusOnModalTabs()
        } else {
            this.setFocusOnMainTabs()
        }
        const redirectUrl = sessionStorage ? sessionStorage.getItem('redirect_url') : location.origin
        const token = sessionStorage ? sessionStorage.getItem(Store.LR_TOKEN_KEY) : undefined
        const registrationSuccessPage = document.querySelector
            (`.${BasePianoSubscriberService.DOM.class.pageRegistrationSuccess}`)
        const exclusiveNewsletterPage = document.querySelector
            (`.${BasePianoSubscriberService.DOM.class.pageExclusiveNewsletter}`)
        const onboardExclusiveNewsletterPage = document.querySelector
            (`.${BasePianoSubscriberService.DOM.class.pageOnboardExclusiveNewsletter}`)

        // clear the status if the user wanted to subscribe or register
        sessionStorage.removeItem(Store.USER_WANTS)
        sessionStorage.removeItem(Store.PIANO_PARAMS)

        // don't redirect if on the registration success or an exclusive newsletter page
        if (registrationSuccessPage || exclusiveNewsletterPage || onboardExclusiveNewsletterPage) {
            return
        }

        if(redirectUrl) {
            if (token) {
                location.replace(`${LoginRadiusAdapter.ssoRedirectLink}&token=${token}&callback=${redirectUrl}`)
            } else {
                location.replace(redirectUrl)
            }
        }
    }

    /**
     * Shows Piano paywall
     * @param offerId
     * @param templateId
     */
    public showPaywall(offerId: string, templateId: string) {
        window.tp.push(['init', () => {
            window.tp.offer.show({
                displayMode: 'modal',
                offerId,
                showCloseButton: false,
                templateId
            })
        }])
    }

    /**
     * Shows Piano offer
     * @param offerId
     * @param templateId
     * @param displayMode
     * @param containerSelector
     *
     * https://docs.piano.io/javascript-offer
     */
    public showOffer(offerId: string, templateId: string, displayMode: string, containerSelector: string) {
        window.tp.push(['init', () => {
            window.tp.offer.show({
                containerSelector,
                displayMode,
                offerId,
                showCloseButton: false,
                templateId,
            })
        }])
    }

    /**
     * Open the Piano checkout modal
     */
    public showCheckoutModal(offerId: string, termId: string, button: HTMLButtonElement) {
        this.subscribeButton = button
        if (offerId && termId) {
            window.tp.push(['init', () => {
                window.tp.offer.startCheckout({
                    offerId,
                    termId
                })
                this.setFocusOnModalTabs()
            }])
        }
    }

    /**
     * Focus to Piano checkout modal
     */
    public setFocusOnModalTabs() {
        const el = document.querySelectorAll<HTMLElement>(FocusableElements.MODAL)
        if (el) {
            try {
                DOMUtils.disableTabs(FocusableElements.MAINSITE)
                DOMUtils.updateTabs(el, 0)
                el[0].focus()
            } catch (e) {
                logger('error', ['cannot focus to modal', e])
            }
        }
    }

    public setFocusOnMainTabs() {
        DOMUtils.enableTabs(FocusableElements.MAINSITE)
        try {
            if (this.subscribeButton) {
                this.subscribeButton.focus()
            } else {
                const main = document.querySelector<HTMLButtonElement>(FocusableElements.MAIN)
                main.tabIndex = 0
                main.focus()
            }
        } catch (e) {
            logger('error', ['cannot focus to modal', e])
        }
    }

    /**
     * Register a user for one-digital registrant access, if they don't have it yet.
     * @param aid: application aid required for Piano API call
     * https://docs.piano.io/api?endpoint=get~2F~2Fconversion~2Fregistration~2Fcreate
     */
    public async registerForOneDigital(aid: string) {
        const query = {
            aid,
            term_id: this.registrantTermId,
            user_token: this.userToken
        }
        try {
            await this.pianoCallApi(this.pianoEndpoints.register, query)
            window.tp.user.refreshAccessToken(true)
        } catch (e) {
            logger('error', e)
        }
    }

    /**
     * Calls Piano API via global object.
     */
    public async pianoCallApi(endpoint: string, data?: any): Promise<IPianoApiResponse> {
        window.tp = window.tp || []
        logger('debug', 'PianoCallApi: LoginRadius user detected')
        await new Promise<void>((resolve) => {
            window.tp.push(['init', () => {
                logger('debug', 'PianoCallApi: Piano ready')
                resolve()
            }])
        })

        return new Promise((resolve, reject) => {
            try {
                window.tp.api.callApi(endpoint, data, (res: IPianoApiResponse) => {
                    // tslint:disable-next-line:no-magic-numbers
                    if (res.code !== 200 && res.code !== 0) {
                        reject(new Error(`Piano API failure: ${JSON.stringify(res)}`))
                    } else {
                        resolve(res)
                    }
                })
            } catch (e) {
                reject(e)
            }
        })
    }

    /**
     * Adds piano callback handlers using the window.tp object
     * @param vendor - optional, used to specify that the vendor is loginradius to not add a loginRequired handler
     */
    public addPianoHandlers(vendor?: string, exclusiveNewsletter?: IExclusiveNewsletterConfig,
                            abTestEnabled = false, isPaywallWhitelisted = false, isPremium = false,
                            showOnboardPages = false) {

        window.tp.push(['addHandler', 'showOffer', (offerParams: any) => {
            const topCommenterElements: string[] = ['.top-commenter-offer', '.top-commenter-profile-offer']
            if(topCommenterElements.includes(offerParams.containerSelector)) {
                document.dispatchEvent(
                    new CustomEvent('custom-event', {
                        detail: {
                            control_fields: {
                                mparticle: {
                                    keys: {
                                        modal_name: 'modal_name'
                                    },
                                    mp_event_type: 'UserPreference'
                                }
                            },
                            event_name: AnalyticsService.customEventNames.modalView,
                            modal_name: 'top_commenter_offer'
                        }
                    })
                )
            }
        } ])
        // call the confirm_referral endpoint and send an analytic event for each new subscription
        // https://docs.piano.io/callbacks/#completeevent
        window.tp.push(['addHandler','showTemplate', (templateParams: any) => {
            // Your code after template has been shown
            if(templateParams.containerSelector === '.vf-load-more.vf-label-text') {
                document.dispatchEvent(
                    new CustomEvent('custom-event', {
                        detail: {
                            control_fields: {
                                mparticle: {
                                    keys: {
                                        modal_name: 'modal_name'
                                    },
                                    mp_event_type: 'UserPreference'
                                }
                            },
                            event_name: AnalyticsService.customEventNames.modalView,
                            modal_name: 'comment_meter_blocker'
                        }
                    })
                    )

                }
            // set focus to modals
            this.setFocusOnModalTabs()
        }])
        // fires after any element with an external-event directive in a template is triggered.
        // https://docs.piano.io/callbacks/#custom
        window.tp.push(['addHandler', 'customEvent', (event: any) => {
            switch (event.eventName) {
                case 'profile-offer-cta':
                case 'in-comments-offer-cta':
                    sessionStorage.removeItem('redirect_url')
                    this.sendOnPageActionClickEvent('claim_offer', 'top_commenter_offer', event.eventName)
                    break
                // handle remind me later click in top commenter offer
                case 'top-commenter-remind-me-later-cta':
                    if(this.uid) {
                        this.ignore_offer().then((res)=> {
                            if(res) {
                                const topCommenter = document.querySelector('.top-commenter-offer') as HTMLElement
                                if(topCommenter) {
                                    topCommenter.innerHTML=''
                                    topCommenter.classList.add('hidden')
                                    this.sendOnPageActionClickEvent('remind_me_later', 'top_commenter_offer',
                                    'in-comments-offer-cta')
                                }
                                const topCommenterReminder= document.querySelector(
                                    '.top-commenter-reminder') as HTMLElement
                                if(topCommenterReminder) {
                                    topCommenterReminder.classList.remove('hidden')
                                    topCommenterReminder.setAttribute('data-evt-skip-click','true')
                                    topCommenterReminder.click()
                                }
                            }
                        }).catch()
                    }
                    break
                case 'top-commenter-close-remind-me-later-cta':
                    const closeTopCommenterReminder= document.querySelector('.top-commenter-reminder') as HTMLElement
                    if(closeTopCommenterReminder) {
                        closeTopCommenterReminder.innerHTML=''
                        closeTopCommenterReminder.classList.add('hidden')
                    }
                    break
                case 'register-cta':
                    LoginService.sendClickEvent('Create an Account', 'link', 'comment-blocker-cta', '/register')
                    break
                case 'login-cta':
                    LoginService.sendClickEvent('Sign in', 'link', 'comment-blocker-cta', '/sign-in')
                    break
            }
        }])
        window.tp.push(['addHandler', 'checkoutComplete', async (conversion: any) => {
            const abTestName = (document.cookie.split(';').find((c) =>
                c.trim().startsWith(`${Store.SOPHI_AB_TEST_NAME}=`)) || '').trim().split('=').pop() || undefined
            const abTestAssignedGroup = (document.cookie.split(';').find((c) =>
                c.trim().startsWith(`${Store.SOPHI_AB_TEST}=`)) || '').trim().split('=').pop() || undefined
            const variantName = (abTestName && abTestAssignedGroup) ?
                `${abTestName}:${abTestAssignedGroup}` : undefined
            const resourceId = conversion.rid
            const profile = LocalStore.get<IProfile>(Store.ID)
            const email = (profile && profile.email) ? profile.email : ''

            if (exclusiveNewsletter && exclusiveNewsletter.resId &&
                exclusiveNewsletter.listId && resourceId === exclusiveNewsletter.resId) {
                // Add email address to exclusive newsletter Sailthru list
                if (email) {
                   await SailthruAdapter.addToEmailList(email, exclusiveNewsletter.listId)
                }
            }

            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                variant_name: 'variant_name',
                            },
                            mp_event_type: 'UserContent'
                        },
                        permutive: {}
                    },
                    event_name: AnalyticsService.customEventNames.subscription,
                    variant_name: variantName
                }
            }))

            // remove profile to force update
            localStorage.removeItem(Store.ID)
        }])

        // send an analytic event for checkout modal
        // https://docs.piano.io/callbacks/#startevent
        window.tp.push(['addHandler', 'startCheckout', () => {
            this.setFocusOnModalTabs()
        }])
        // send analytic event for checkout state change
        // https://docs.piano.io/callbacks/#statechangeevent
        window.tp.push(['addHandler', 'checkoutStateChange', (event: any) => {
            if(event.stateName !== 'offer') {
                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                    detail: {
                        control_fields: {
                            mparticle: {
                                keys: {
                                    modal_name: 'modal_name'
                                },
                                mp_event_type: 'UserPreference'
                            },
                            permutive: {
                                keys: {
                                    modal_name: 'modal_name'
                                }
                            }
                        },
                        event_name: AnalyticsService.customEventNames.modalView,
                        modal_name: event.stateName === 'state2' ? 'start_checkout' : event.stateName
                    }
                }))
            }
        }])

        // reload the page after completing checkout and closing the modal
        // https://docs.piano.io/callbacks/#closeevent
        window.tp.push(['addHandler', 'checkoutClose', (checkoutCloseData: any) => {
            const exclusiveNewsletterPage = document.querySelector
                (`.${BasePianoSubscriberService.DOM.class.pageExclusiveNewsletter}`)
            const onboardExclusiveNewsletterPage = document.querySelector(
                `.${BasePianoSubscriberService.DOM.class.pageOnboardExclusiveNewsletter}`)
            let onboardingFlow = showOnboardPages
            const subscribeType = sessionStorage.getItem(Store.SUBSCRIBE_TYPE)

            // session Storage - clear status of the items
            sessionStorage.removeItem(Store.USER_WANTS)
            sessionStorage.removeItem(Store.PIANO_PARAMS)
            sessionStorage.removeItem(Store.SUBSCRIBE_TYPE)

            // Skip onboarding flow if it is exclusive newsletter checkout
            if (exclusiveNewsletterPage || subscribeType === 'newsletter') {
                onboardingFlow = false
            }

            if (checkoutCloseData.state !== PianoCheckoutState.CHECKOUT_COMPLETED) {
                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                        detail: {
                            checkout_state: checkoutCloseData.state,
                            control_fields: {
                                mparticle: {
                                    keys: {
                                        checkout_state: 'checkout_state'
                                    },
                                    mp_event_type: 'UserContent'
                                }
                            },
                            event_name: AnalyticsService.customEventNames.checkoutClosed,
                        }
                    }
                ))
            }

            switch (checkoutCloseData.state) {
                case PianoCheckoutState.CHECKOUT_COMPLETED:
                    if (onboardExclusiveNewsletterPage) {
                        const redirectUrl = LoginService.getRedirectUrl(location.origin)
                        location.replace(redirectUrl)
                        break
                    }

                    if (onboardingFlow) {
                        // Onboarding flow is ON
                        location.replace('/onboard-follow-author/')
                        break
                    } else {
                        // Onboarding flow is OFF
                        this.closeCheckoutModal()
                        break
                    }
                case PianoCheckoutState.ALREADY_HAS_ACCESS:
                case PianoCheckoutState.VOUCHER_REDEMPTION_COMPLETED:
                    window.location.reload()
                    this.closeCheckoutModal()
                    break
                case PianoCheckoutState.CLOSE:
                    // User did not complete the purchase and simply closed the modal
                    // TODO: show more attractive offer
                    // if site behind disabled, enable
                    this.closeCheckoutModal()
                    break
                }
            // if a button was stored, return focus or focus main
        }])

        // send an analytics event when encountering a checkout error
        window.tp.push(['addHandler', 'checkoutPaymentError', (errorData: any) => {
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                error_message: 'error_message',
                                form_name: 'form_name',
                            },
                            mp_event_type: 'UserContent'
                        }
                    },
                    error_message: `${errorData.message}`,
                    event_name: AnalyticsService.customEventNames.submissionError,
                    form_name: 'piano-checkout'
                }
            }))
        }])

        // https://docs.piano.io/callbacks/#experienceexecute
        window.tp.push(['addHandler', 'experienceExecute', (experience: any) => {
            let variantName
            const experienceMeterExpiredObject = experience.result.events.filter((event: { eventType: string }) =>
                event.eventType === 'meterExpired')
            if (!this.uid) {
                this.uid = experience.user.uid
            }
            const meterExpired = experienceMeterExpiredObject.length > 0
            const sophiAbTestName = (document.cookie.split(';').find((c) =>
                c.trim().startsWith(`${Store.SOPHI_AB_TEST_NAME}=`)) || '').trim().split('=').pop() || undefined
            const sophiAbTestAssignedGroup = (document.cookie.split(';').find((c) =>
                c.trim().startsWith(`${Store.SOPHI_AB_TEST}=`)) || '').trim().split('=').pop() || undefined
            const sophiVariantName = (sophiAbTestName && sophiAbTestAssignedGroup) ?
                    `${sophiAbTestName}:${sophiAbTestAssignedGroup}` : undefined

            // Sophi AB test
            if (sophiAbTestAssignedGroup && sophiAbTestAssignedGroup !== AbTestAssignedGroup.VARIANT &&
                !isPaywallWhitelisted && !isPremium
            ) {
                let trace = ''
                let context = ''
                let inputs = ''

                // Get trace, context and inputs data from Sophi
                if (window.sophi && window.sophi.quickStart && window.sophi.quickStart.decision) {
                    const decision = window.sophi.quickStart.decision
                    trace = decision.trace
                    context = decision.context
                    inputs = decision.inputs
                }

                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                    detail: {
                        context,
                        control_fields: {
                            mparticle: {
                                keys: {
                                    context: 'context',
                                    inputs: 'inputs',
                                    meter_name: 'meter_name',
                                    trace: 'trace',
                                    variant_name: 'variant_name'
                                },
                                mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                                mp_event_type: 'UserPreference'
                            }
                        },
                        event_name: AnalyticsService.customEventNames.meterExecute,
                        inputs,
                        meter_name: 'sophi_meter',
                        trace,
                        variant_name: sophiVariantName
                    }
                }))
            }

            // taking care of piano AB testing
            const profile = LocalStore.get<IProfile>(Store.ID)

            if (abTestEnabled && experience.result.experiences.length > 0) {
                const experienceAB = experience.result.events.filter((event: { eventType: string }) =>
                    event.eventType === 'experienceExecute')
                if (experienceAB && experienceAB.length > 0 &&
                    experienceAB[0].eventExecutionContext.splitTests.length > 0) {
                    variantName = experienceAB[0].eventExecutionContext.splitTests[0].variantName
                }

                if (window.tp.tags === 'metered' && !profile.adLight &&
                    experienceAB && experienceAB.length > 0 && !meterExpired) {
                    const currentMeterName = experienceAB[0].eventExecutionContext.currentMeterName || 'undefined'
                    logger('debug', `Current Meter Name ${currentMeterName}`)
                    document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                        detail: {
                            control_fields: {
                                mparticle: {
                                    keys: {
                                        meter_name: 'meter_name',
                                        variant_name: 'variant_name'
                                    },
                                    mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                                    mp_event_type: 'UserPreference'
                                }
                            },
                            event_name: AnalyticsService.customEventNames.meterExecute,
                            meter_name: `${currentMeterName}`,
                            variant_name: variantName
                        }
                    }))
                }
            }
            if (!meterExpired) {
                const ads = document.querySelectorAll('.lazy-loaded-ad')
                const adElements: Element[] = []

                setTimeout(() => {
                    ads.forEach((adEl) => {
                        const adContent = document.createElement('div')
                        adContent.innerHTML = adEl.innerHTML
                        adEl.replaceWith(adContent.firstChild)
                        adElements.push(adEl)
                    })
                    dispatchEvent(
                        new CustomEvent(AdEvents.SLOT_ADDED, {
                            detail: { el: adElements },
                        })
                    )
                }, 0)
            }
            if (experienceMeterExpiredObject && meterExpired) {
                    const meterData = experienceMeterExpiredObject[0].eventParams
                    const meterName = meterData.meterName
                    const registerMeter = 'REGMETER'
                    const modalName = (meterName === registerMeter) ? 'regwall_limit_reached' : 'paywall_limit_reached'

                    let meterExpiredVariant

                    if (sophiVariantName) {
                        meterExpiredVariant = sophiVariantName // Sophi A/B test
                    } else if (variantName) {
                        meterExpiredVariant = variantName // Piano A/B test
                    } else {
                        meterExpiredVariant = undefined // No unit tests
                    }

                    if (window.demeter) {
                        const wallEvent = (meterName === registerMeter) ? WallType.REGWALL : WallType.PAYWALL

                        // Dispatch subscription wall event to Sophi
                        window.demeter('wall', {
                            args: wallEvent
                        })
                        logger('info', `Piano - Sophi ${wallEvent} event dispatched`)
                    }

                    document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                        detail: {
                            control_fields: {
                                mparticle: {
                                    keys: {
                                        meter_name: 'meter_name',
                                        modal_name: 'modal_name',
                                        variant_name: 'variant_name'
                                    },
                                    mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                                    mp_event_type: 'UserPreference'
                                },
                                permutive: {
                                    keys: {
                                        modal_name: 'modal_name'
                                    }
                                }
                            },
                            event_name: AnalyticsService.customEventNames.modalView,
                            meter_name: (sophiAbTestAssignedGroup) ? 'sophi_meter' : meterName,
                            modal_name: modalName,
                            variant_name: meterExpiredVariant
                        }
                    }))
                    this.handleMeterExpired(meterData)
                }
            // send an analytic event for premium content
            const premium = experience.result.events.filter((event: {eventModuleParams: { moduleName: string }}) =>
                    event.eventModuleParams.moduleName === PianoModuleName.PREMIUM_SHOW_OFFER)
            if (premium.length > 0) {
                    document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                        detail: {
                            control_fields: {
                                mparticle: {
                                    keys: {
                                        modal_name: 'modal_name',
                                    },
                                    mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                                    mp_event_type: 'UserPreference'
                                }
                            },
                            event_name: AnalyticsService.customEventNames.modalView,
                            modal_name: 'paywall_premium_content'
                        }
                    }))
                }
            // send an analytic event for registration only
            const registrationOnly = experience.result.events.filter(
                (event: {eventModuleParams: { moduleName: string }}) =>
                    event.eventModuleParams.moduleName === PianoModuleName.REGISTRATION_ONLY_USER_SEGMENT)
            if (registrationOnly.length > 0) {
                    document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                        detail: {
                            control_fields: {
                                mparticle: {
                                    keys: {
                                        modal_name: 'modal_name',
                                    },
                                    mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                                    mp_event_type: 'UserPreference'
                                }
                            },
                            event_name: AnalyticsService.customEventNames.modalView,
                            modal_name: 'registration_only'
                        }
                    }))
                }

            this.handleExperienceExecute(experience)
            }])

        // modal_view event for article blocker is now fired directly from piano composer flow for Telegraph Journal

        // https://docs.piano.io/callbacks/#activeevent
        window.tp.push(['addHandler', 'meterActive', (meterData: any) => {
            this.handleMeterActive(meterData)
        }])

        // send an analytic event for paywall limit modal
        // https://docs.piano.io/callbacks/#expiredevent
        // moved to experience execute

        if (vendor === 'loginradius') {
            window.tp.push(['addHandler', 'loginRequired', (params: any) => {
                // Redirect to login page
                const redirectUrl = location.origin
                const screen = (params && params.startScreen) ? params.startScreen : ''
                const exclusiveNewsletterPage = document.querySelector(
                    `.${BasePianoSubscriberService.DOM.class.pageExclusiveNewsletter}`)
                if (sessionStorage) {
                    LoginService.setRedirectUrl(location.href)
                    // store params of offer in storage before redirect
                    if (params && params.offerId && params.termId) {
                        sessionStorage.setItem(Store.PIANO_PARAMS, JSON.stringify(params))
                        sessionStorage.setItem(Store.USER_WANTS, 'subscribe')

                        if (exclusiveNewsletterPage) {
                            sessionStorage.setItem(Store.SUBSCRIBE_TYPE, 'newsletter')
                        }
                    }
                }
                if (screen === SSOScreen.LOG_IN) {
                    location.assign(`${redirectUrl}/sign-in`)
                } else {
                    location.assign(`${redirectUrl}/register`)
                }
            }])
        }
    }

    /**
     * Push Piano tags
     */
    public async pianoPushTags(isPremium: boolean, isPaywallWhitelisted: boolean, isRegistration: boolean) {
        const tags: string[] = []
        if (isPremium) {
            tags.push('premium_locked')
        } else if (isRegistration) {
            tags.push('registration_locked')
        } else if (isPaywallWhitelisted) {
            tags.push('not_metered')
        } else {
            tags.push('metered')
        }

        await this.pianoReady

        return new Promise<void>((resolve, reject) => {
            try {
                window.tp.push(['setTags', tags])
                resolve()
            } catch (e) {
                reject(e)
            }
        })
    }

    /**
     * Push product logo url for Piano templates
     */
    public pianoPushLogoUrl(logoUrl: IIdentityLogoConfig) {
        window.tp.push(['setCustomVariable', 'logoUrl', logoUrl.default])
        logger('debug', 'logoURL: ' + logoUrl)
    }

    public handleMeterActive(meterData: any) {
        this.meterData = {
            expired: false,
            maxViews: meterData.maxViews,
            views: meterData.views
        }
        this.logMeterData()
        document.dispatchEvent(new CustomEvent(DOMEvents.PIANO_METER_EVENT,
            { detail: { meterData: this.meterData } }))
    }

    public handleMeterExpired(meterData: any) {
        this.meterData = {
            expired: true,
            maxViews: meterData.maxViews,
            views: meterData.views
        }
        this.logMeterData()
        document.dispatchEvent(new CustomEvent(DOMEvents.PIANO_METER_EVENT,
            { detail: { meterData: this.meterData } }))
    }
    protected handleExperienceExecute(experience: any) {
        this.pianoUser = {
            email: experience.user.email
        }
        this.logMeterData()
    }

    protected logMeterData() {
        // log meter data in console, when all data is available
        // log only on stage/dev or if debug=logging param is in URL for prod
        if (this.pianoUser && this.meterData && (__NON_PROD || loggerEnabled())) {
            const isUserLoggedIn = this.pianoUser.email.toLowerCase() !== 'n/a'
            const logMessagePrefix = isUserLoggedIn ? 'Paywall' : 'Registration'
            const logMessageSuffix = this.meterData.expired ? 'hard gate' : 'article counter'
            const logUserEmail = isUserLoggedIn ? this.pianoUser.email : 'not logged in'
            const logMeterData = `${this.meterData.views} of ${this.meterData.maxViews} free metered articles read`
            const css = isUserLoggedIn ?
                'padding: 3px 10px 3px 10px; font-size:15px; background-color:green; color:white;' :
                'padding: 3px 10px 3px 10px; font-size:15px; background-color:red; color:black;'
            const message = `${logMessagePrefix} ${logMessageSuffix} - ${logUserEmail} - ${logMeterData}`
            console.log('%c%s', css, message)
        }
    }
    /*
    * Api request to update top commenter custum field to ignore
    */
    protected async ignore_offer(): Promise<boolean> {
        const response = await fetch( '/api-root/subscription/piano/ignore-top-commenter-offer/' , {
            body: new URLSearchParams({
                aid: window.tp.aid,
                uid: this.uid,
            }),
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            method: 'POST'
        })
        if (response.ok) {
            return true
        } else {
            return false
        }
    }

    /**
     * Send on_page_action_click event
     * @param action_type
     * @param context
     * @param occurence
     */
    protected sendOnPageActionClickEvent(actionType: string, context: string, occurence: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                action_type: actionType,
                context,
                control_fields: {
                    mparticle: {
                        keys: {
                            action_type: 'action_type',
                            context: 'context',
                            occurrence: 'occurrence'
                        },
                        mp_event_type: 'Other',
                    },
                },
                event_name: AnalyticsService.customEventNames.onPageActionClick,
                occurrence: occurence
            }
        }))
    }

}
